import _slicedToArray from "/builds/vtblife/frontend/common/gatsby-party/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Icon from '@vtblife/uikit-icons';
import { IMask } from 'react-imask';
import copyToClipboard from 'copy-to-clipboard';
import normalizeCSSModules from '../../utils/css-modules';
import styles from './input.module.css';
import { useTransformations, useInputHandlers, useContent, useInputId, useInputStatus, useIMask } from './utils';
import Content from './content';
import InputIcon from './icon';
import { noop } from '../../utils/function';
import { useGroup } from '../group/utils';
import Label from './label';
import { handleKeyPressEnter } from '../../utils/handle-keypress-enter';
import { Tooltip, useTooltip } from '../tooltip';
var MODAL_TRANSITION_TIME = 240 + 20;
var s = normalizeCSSModules(styles);
var defaultResolver = function defaultResolver(v) {
  return v;
};
var Loader = function Loader(_ref) {
  var fade = _ref.fade;
  return React.createElement("svg", {
    className: classNames(fade && s.fade),
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement("path", {
    d: "M11 3a1 1 0 011-1 10 10 0 0110 10 1 1 0 11-2 0 7.999 7.999 0 00-8-8 1 1 0 01-1-1z",
    fill: "currentColor"
  }));
};
var Input = function Input(props) {
  var _props$autoComplete = props.autoComplete,
    autoComplete = _props$autoComplete === void 0 ? 'off' : _props$autoComplete,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    _props$background = props.background,
    background = _props$background === void 0 ? 'primary' : _props$background,
    children = props.children,
    dataTest = props.dataTest,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$error = props.error,
    error = _props$error === void 0 ? false : _props$error,
    _props$floating = props.floating,
    floating = _props$floating === void 0 ? true : _props$floating,
    formatter = props.formatter,
    gtm = props.gtm,
    hint = props.hint,
    _props$id = props.id,
    externalId = _props$id === void 0 ? true : _props$id,
    _props$isCopyable = props.isCopyable,
    isCopyable = _props$isCopyable === void 0 ? false : _props$isCopyable,
    copyValue = props.copyValue,
    _props$info = props.info,
    info = _props$info === void 0 ? null : _props$info,
    _props$inputMode = props.inputMode,
    inputMode = _props$inputMode === void 0 ? 'text' : _props$inputMode,
    _props$label = props.label,
    label = _props$label === void 0 ? '' : _props$label,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$loaderFade = props.loaderFade,
    loaderFade = _props$loaderFade === void 0 ? false : _props$loaderFade,
    max = props.max,
    maxLength = props.maxLength,
    min = props.min,
    name = props.name,
    imask = props.imask,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? function () {} : _props$onChange,
    _props$onReset = props.onReset,
    onReset = _props$onReset === void 0 ? function () {} : _props$onReset,
    _props$defaultResetVa = props.defaultResetValue,
    defaultResetValue = _props$defaultResetVa === void 0 ? '' : _props$defaultResetVa,
    parser = props.parser,
    pattern = props.pattern,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? '' : _props$placeholder,
    _props$postfix = props.postfix,
    postfix = _props$postfix === void 0 ? '' : _props$postfix,
    _props$prefix = props.prefix,
    prefix = _props$prefix === void 0 ? '' : _props$prefix,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    required = props.required,
    _props$size = props.size,
    externalSize = _props$size === void 0 ? 's' : _props$size,
    _props$spaced = props.spaced,
    spaced = _props$spaced === void 0 ? false : _props$spaced,
    _props$type = props.type,
    type = _props$type === void 0 ? 'text' : _props$type,
    _props$valid = props.valid,
    valid = _props$valid === void 0 ? false : _props$valid,
    externalValue = props.value,
    _props$warning = props.warning,
    warning = _props$warning === void 0 ? false : _props$warning,
    _props$passRequiredTo = props.passRequiredToHTMLInput,
    passRequiredToHTMLInput = _props$passRequiredTo === void 0 ? true : _props$passRequiredTo,
    onClick = props.onClick,
    inputRef = props.inputRef,
    wrapperRef = props.wrapperRef,
    containerRef = props.containerRef;
  var _props$autofocus = props.autofocus,
    autofocus = _props$autofocus === void 0 ? false : _props$autofocus,
    _props$readonly = props.readonly,
    readonly = _props$readonly === void 0 ? false : _props$readonly,
    _props$success = props.success,
    success = _props$success === void 0 ? false : _props$success;
  var _useTransformations = useTransformations(formatter, parser),
    format = _useTransformations.format,
    parse = _useTransformations.parse;
  var handlers = useInputHandlers(props);
  var copySuccess = useTooltip();
  var input = useRef(null);
  var container = useRef(null);
  var hasImask = imask && Object.keys(imask).length > 0;
  var initValue = useMemo(function () {
    var resolver = defaultResolver;
    if (hasImask) {
      var _masked = IMask.createMask(imask);
      resolver = _masked.resolve.bind(_masked);
    }
    return resolver;
  }, [hasImask, JSON.stringify(imask)]);
  var _useState = useState(initValue(format(externalValue))),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isCopySuccess = _useState4[0],
    setIsCopySuccess = _useState4[1];
  var id = useInputId(externalId);
  var group = useGroup();
  var size = group.present && group.size || externalSize;
  autofocus = autofocus || autoFocus;
  readonly = readonly || readOnly;
  useEffect(function () {
    if (!(input === null || input === void 0 ? void 0 : input.current)) {
      return;
    }
    var isAlreadyFocused = document.activeElement === input.current;
    var isShouldFocused = autofocus && !isAlreadyFocused;
    if (isShouldFocused) {
      setTimeout(function () {
        var _a;
        return (_a = input === null || input === void 0 ? void 0 : input.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, MODAL_TRANSITION_TIME);
    }
  }, [autofocus, input]);
  var reset = !disabled && !readonly && !!(onReset && value.length > 0);
  var handleClick = useCallback(function (event) {
    var _a;
    if (event.target === container.current) {
      (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  }, [onClick]);
  var handleCopy = useCallback(function () {
    try {
      navigator.clipboard.writeText(copyValue || value);
    } catch (_error) {
      copyToClipboard(copyValue || value);
    }
    setIsCopySuccess(true);
    setTimeout(function () {
      return setIsCopySuccess(false);
    }, 2000);
  }, [copyValue, value]);
  var handleValueChange = useCallback(function (newValue, event) {
    var text = format(newValue);
    if (value !== text) {
      onChange(parse(newValue), event);
    }
    setValue(text);
  }, [format, parse, onChange, value]);
  var handleImaskOnAccept = useCallback(function (v, _mask, e) {
    return handleValueChange(v, e);
  }, [handleValueChange]);
  var handleInputOnChange = useCallback(function (event) {
    return handleValueChange(event.target.value, event);
  }, [handleValueChange]);
  var _useIMask = useIMask(imask, {
      onAccept: handleImaskOnAccept
    }),
    masked = _useIMask.ref,
    maskRef = _useIMask.maskRef;
  var syncValueWithMask = useCallback(function (update) {
    if (!maskRef || !maskRef.current) {
      return update;
    }
    maskRef.current.unmaskedValue = update;
    return maskRef.current.value;
  }, [maskRef]);
  useEffect(function () {
    setValue(syncValueWithMask(format(externalValue)));
  }, [externalValue, format, syncValueWithMask]);
  var handleOnPaste = useCallback(function (event) {
    event.preventDefault();
    if (disabled || readonly) {
      return;
    }
    var raw = (event.clipboardData || window.clipboardData).getData('text/plain');
    var _event$target = event.target,
      value = _event$target.value,
      selectionStart = _event$target.selectionStart,
      selectionEnd = _event$target.selectionEnd;
    var targetValue = value.slice(0, selectionStart) + raw + value.slice(selectionEnd);
    var text = syncValueWithMask(format(targetValue));
    if (value !== text) {
      onChange(parse(text), event);
    }
    setValue(text);
    return false;
  }, [format, syncValueWithMask, onChange, parse, disabled, readonly]);
  var handleReset = useCallback(function (event) {
    var _a;
    if (hasImask) {
      setValue(syncValueWithMask(defaultResetValue));
    } else {
      setValue(defaultResetValue);
      onChange(parse(defaultResetValue), event);
    }
    if (onReset) {
      onReset(event);
    }
    (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, [onReset, defaultResetValue, syncValueWithMask, hasImask, onChange, parse]);
  success = success || valid;
  var status = useInputStatus(success, error, warning);
  var content = useContent(children);
  var assignInputRef = useCallback(function (el) {
    input.current = el;
    if (hasImask) {
      masked.current = el;
    }
    if (inputRef) {
      inputRef.current = el;
    }
  }, [masked, hasImask, inputRef]);
  var assignContainerRef = useCallback(function (el) {
    container.current = el;
    if (containerRef) {
      containerRef.current = el;
    }
  }, [containerRef]);
  var attrs = Object.assign({}, passRequiredToHTMLInput && {
    required: required
  });
  return React.createElement("div", {
    className: classNames(s.wrapper, spaced && s.spaced, group.present && !group.first && s.group),
    ref: wrapperRef,
    style: Object.assign(Object.assign({}, group.present && {
      flex: group.width
    }), group.present && {
      maxWidth: group.maxWidth
    })
  }, React.createElement("span", {
    className: classNames(s.container, s[size], warning && s.warning, error && s.error, success && s.success, !label && size === 'l' && s.single, label && size !== 'l' && s.labeled, floating && size === 'l' && s.floating, disabled && s.disabled, readonly && s.readonly),
    ref: assignContainerRef,
    "data-gtm": gtm,
    "data-test": dataTest,
    onClick: handleClick
  }, content.start, prefix && React.createElement("span", {
    className: classNames(s.start, s.prefix, value.length > 0 && s.active, readonly && s.readonly)
  }, prefix), React.createElement("input", Object.assign({
    autoComplete: autoComplete,
    autoCorrect: "off",
    className: classNames(s.control, value.length === 0 && s.empty),
    disabled: disabled,
    id: id,
    inputMode: inputMode,
    maxLength: maxLength,
    name: name,
    pattern: pattern,
    placeholder: placeholder,
    readOnly: readonly,
    size: 1,
    type: type,
    min: min,
    max: max,
    value: value,
    autoFocus: autofocus,
    ref: assignInputRef,
    onChange: hasImask ? noop : handleInputOnChange,
    onPaste: handleOnPaste
  }, attrs, handlers)), postfix && React.createElement("span", {
    "data-addon-end": true,
    className: classNames(s.end, s.postfix)
  }, postfix), (status.icon || loading) && React.createElement("span", {
    "data-addon-end": true,
    className: classNames(s.end, loading ? s.loader : s.status),
    ref: status.ref
  }, loading ? React.createElement(Loader, {
    fade: loaderFade
  }) : React.createElement(Icon, {
    name: status.icon
  })), isCopyable && (copyValue || value) && React.createElement("span", {
    "data-addon-end": true,
    className: classNames(s.end, s.icon, s.copy),
    onClick: handleCopy,
    onKeyPress: handleKeyPressEnter(handleCopy),
    role: "button",
    tabIndex: 0,
    "aria-label": "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C",
    "data-test": "copy-icon",
    ref: copySuccess.registerControl
  }, React.createElement(Icon, {
    name: isCopySuccess ? 'check' : 'copy'
  }), isCopySuccess && React.createElement(Tooltip, {
    trigger: "static",
    to: copySuccess.to,
    direction: "up",
    placement: "body"
  }, "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u043E")), reset && React.createElement("span", {
    "data-addon-end": true,
    className: classNames(s.end, s.icon, s.reset),
    onClick: handleReset,
    onKeyPress: handleKeyPressEnter(handleReset),
    role: "button",
    tabIndex: -1,
    "aria-label": "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C",
    "data-test": "reset-icon"
  }, React.createElement(Icon, {
    name: "times-circle"
  })), info && React.createElement("span", {
    "data-addon-end": true,
    className: classNames(s.end, s.icon),
    ref: info
  }, React.createElement(Icon, {
    name: "information-circle"
  })), content.end, label && React.createElement(Label, {
    container: container,
    size: size,
    content: content,
    info: info,
    reset: reset,
    postfix: postfix,
    id: id,
    required: required,
    status: status
  }, label), React.createElement("span", {
    className: classNames(s.bg, s[background], (!group.present || group.present && group.first) && s.roundedLeft, (!group.present || group.present && group.last) && s.roundedRight)
  })), hint && React.createElement("span", {
    className: classNames(s.hint, error && s.error, warning && s.warning, success && s.success),
    "data-test": error ? 'error' : warning ? 'warning' : success ? 'success-hint' : 'hint'
  }, hint));
};
Input.Content = Content;
Input.Icon = InputIcon;
export default Input;